import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PayPalCheckoutItem } from '@/components/PayPalButton/PayPalForm';
import { getCartItemKey } from '@/utils/cart-math';

type CartState = {
  items: PayPalCheckoutItem[];
  isSheetOpen: boolean;
  toastMessage: string | null;
};

const initialState: CartState = {
  items: [],
  isSheetOpen: false,
  toastMessage: null,
};

const cartSlice = createSlice({
  name: `cart`,
  initialState,
  reducers: {
    addItem(state, action: PayloadAction<PayPalCheckoutItem>) {
      const key = getCartItemKey(action.payload);
      const existingIndex = state.items.findIndex(
        (item) => getCartItemKey(item) === key,
      );
      if (existingIndex >= 0) {
        const existing = state.items[existingIndex];
        existing.quantity += 1;
        // Preorder status can change after an item is first added (e.g. a
        // book that wasn't a preorder yet when it went into the cart).
        // Refresh it from the latest catalog data instead of freezing the
        // stale value from the first add.
        existing.isPreorder = action.payload.isPreorder;
        existing.preOrderMessage = action.payload.preOrderMessage;
      } else {
        state.items.push({ ...action.payload, quantity: 1 });
      }
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter(
        (item) => getCartItemKey(item) !== action.payload,
      );
    },
    updateQuantity(
      state,
      action: PayloadAction<{ itemKey: string; quantity: number }>,
    ) {
      const { itemKey, quantity } = action.payload;
      const item = state.items.find((i) => getCartItemKey(i) === itemKey);
      if (item) {
        item.quantity = Math.max(1, quantity);
      }
    },
    clearCart(state) {
      state.items = [];
    },
    hydrateCart(state, action: PayloadAction<PayPalCheckoutItem[]>) {
      state.items = action.payload;
    },
    openSheet(state) {
      state.isSheetOpen = true;
    },
    closeSheet(state) {
      state.isSheetOpen = false;
    },
    showToast(state, action: PayloadAction<string>) {
      state.toastMessage = action.payload;
    },
    clearToast(state) {
      state.toastMessage = null;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  hydrateCart,
  openSheet,
  closeSheet,
  showToast,
  clearToast,
} = cartSlice.actions;

export const selectCartItems = (state: { cart: CartState }) => state.cart.items;
export const selectCartCount = (state: { cart: CartState }) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
export const selectIsSheetOpen = (state: { cart: CartState }) =>
  state.cart.isSheetOpen;
export const selectToastMessage = (state: { cart: CartState }) =>
  state.cart.toastMessage;

export default cartSlice.reducer;
